import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";

/**
 * =========================================
 * GET CURRENCIES
 * =========================================
 */
export const getCurrencies = createAsyncThunk(
  "currentCurrency/getCurrencies",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/currencies");

      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error("Currencies API error:", err);

      return rejectWithValue(
        err.response?.data ||
          err.message ||
          "Failed to fetch currencies."
      );
    }
  }
);

/**
 * =========================================
 * DETECT COUNTRY
 * =========================================
 */
export const detectCountry = createAsyncThunk(
  "currentCurrency/detectCountry",
  async (_, { getState, rejectWithValue }) => {
    try {
      const currencies =
        getState().currentCurrency?.currencies || [];

      console.log("Currencies:", currencies);

      const response = await fetch(
        "https://ipapi.co/json/",
        {
          signal: AbortSignal.timeout(10000),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Country API failed: ${response.status}`
        );
      }

      const country = await response.json();

      console.log("IP country:", country);

      const countryCode =
        country?.country_code?.toUpperCase() || "";

      console.log(
        "Detected country code:",
        countryCode
      );

      const currency = currencies.find(
        (item) =>
          item?.country_code?.toUpperCase() ===
          countryCode
      );

      console.log(
        "Matched currency:",
        currency
      );

      return {
        currentCountry: {
          country_code: countryCode,
          country_name:
            country?.country_name || "",
          currency: country?.currency || "",
        },

        detectedCurrency: currency || null,
      };
    } catch (err) {
      console.error(
        "Country detection error:",
        err
      );

      return rejectWithValue(
        err.message ||
          "Unable to detect your country."
      );
    }
  }
);

// export const detectCountry = createAsyncThunk(
//   "currentCurrency/detectCountry",
//   async (currencyList, { getState, rejectWithValue }) => {
//     try {
//       const currencies =
//         currencyList ||
//         getState().currentCurrency?.currencies ||
//         [];

//       console.log(
//         "Testing with static India country"
//       );

//       /**
//        * STATIC COUNTRY FOR TESTING
//        */
//       const countryCode = "IN";

//       const countryName = "India";

//       const currencyCode = "INR";

//       /**
//        * Find India from your currency API response
//        */
//       const currency = currencies.find(
//         (item) =>
//           item?.country_code?.toUpperCase() ===
//           countryCode
//       );

//       console.log(
//         "Static country:",
//         countryName
//       );

//       console.log(
//         "Static country code:",
//         countryCode
//       );

//       console.log(
//         "Matched currency:",
//         currency
//       );

//       /**
//        * If India exists in the currencies API,
//        * use that currency.
//        *
//        * Otherwise create a temporary static
//        * currency object for testing.
//        */
//       const detectedCurrency =
//         currency || {
//           id: "static-india",
//           country_code: "IN",
//           country_name: "India",
//           currency_code: "INR",
//           currency_name: "Indian Rupee",
//           currency_symbol: "₹",
//           flag: "🇮🇳",
//           exchange_rate: 1,
//           is_active: true,
//           is_default: false,
//           is_manual_rate: false,
//         };

//       return {
//         currentCountry: {
//           country_code: countryCode,
//           country_name: countryName,
//           currency: currencyCode,
//         },

//         detectedCurrency,
//       };
//     } catch (err) {
//       console.error(
//         "Country detection error:",
//         err
//       );

//       return rejectWithValue(
//         err.message ||
//           "Unable to detect your country."
//       );
//     }
//   }
// );

const initialState = {
  currencies: [],

  loading: false,
  detectingCountry: false,

  error: null,

  currentCountry: null,
  detectedCurrency: null,
  selectedCurrency: null,

  status: "idle",
};

const currentCountrySlice = createSlice({
  name: "currentCurrency",

  initialState,

  reducers: {
    /**
     * =========================================
     * SET SELECTED CURRENCY
     * =========================================
     */
    setSelectedCurrency: (state, action) => {
      const currency = action.payload;

      if (!currency) {
        return;
      }

      if (typeof window !== "undefined") {
        localStorage.setItem(
          "selected_currency",
          JSON.stringify(currency)
        );

        if (currency.country_code) {
          localStorage.setItem(
            "currency_code",
            currency.currency_code
          );
        }
      }

      state.selectedCurrency = currency;

      /**
       * Once user selects currency,
       * detected currency is no longer needed.
       */
      state.detectedCurrency = null;
    },

    /**
     * =========================================
     * INITIALIZE SELECTED CURRENCY
     * =========================================
     */
    initializeSelectedCurrency: (state) => {
      if (typeof window === "undefined") {
        return;
      }

      const savedCurrency =
        localStorage.getItem(
          "selected_currency"
        );

      if (!savedCurrency) {
        state.selectedCurrency = null;
        return;
      }

      try {
        const currency =
          JSON.parse(savedCurrency);

        state.selectedCurrency = currency;

        console.log(
          "Restored selected currency:",
          currency
        );
      } catch (error) {
        console.error(
          "Invalid saved currency:",
          error
        );

        localStorage.removeItem(
          "selected_currency"
        );

        localStorage.removeItem(
          "currency_code"
        );

        state.selectedCurrency = null;
      }
    },

    /**
     * =========================================
     * UNSET CURRENCY
     * =========================================
     */
    unsetSelectedCurrency: (state) => {
      if (typeof window !== "undefined") {
        localStorage.removeItem(
          "selected_currency"
        );

        localStorage.removeItem(
          "currency_code"
        );
      }

      state.selectedCurrency = null;
      state.detectedCurrency = null;
    },

    /**
     * =========================================
     * RESET STATUS
     * =========================================
     */
    resetCurrencyStatus: (state) => {
      state.loading = false;
      state.detectingCountry = false;
      state.error = null;
      state.status = "idle";
    },
  },

  extraReducers: (builder) => {
    builder

      /**
       * =========================================
       * GET CURRENCIES
       * =========================================
       */

      .addCase(
        getCurrencies.pending,
        (state) => {
          state.loading = true;
          state.error = null;
          state.status = "loading";
        }
      )

      .addCase(
        getCurrencies.fulfilled,
        (state, action) => {
          state.loading = false;
          state.status = "succeeded";

          state.currencies =
            Array.isArray(action.payload)
              ? action.payload
              : [];
        }
      )

      .addCase(
        getCurrencies.rejected,
        (state, action) => {
          state.loading = false;
          state.status = "failed";

          state.currencies = [];

          state.error =
            action.payload ||
            "Failed to fetch currencies.";
        }
      )

      /**
       * =========================================
       * DETECT COUNTRY
       * =========================================
       */

      .addCase(
        detectCountry.pending,
        (state) => {
          state.detectingCountry = true;
          state.error = null;

          console.log(
            "Country detection pending..."
          );
        }
      )

      .addCase(
        detectCountry.fulfilled,
        (state, action) => {
          state.detectingCountry = false;

          state.currentCountry =
            action.payload?.currentCountry ||
            null;

          state.detectedCurrency =
            action.payload?.detectedCurrency ||
            null;

          console.log(
            "Country detection completed:",
            action.payload
          );
        }
      )

      .addCase(
        detectCountry.rejected,
        (state, action) => {
          state.detectingCountry = false;

          state.error =
            action.payload ||
            "Unable to detect your country.";

          console.error(
            "Country detection rejected:",
            action.payload
          );
        }
      );
  },
});

export const {
  setSelectedCurrency,
  initializeSelectedCurrency,
  unsetSelectedCurrency,
  resetCurrencyStatus,
} = currentCountrySlice.actions;

export default currentCountrySlice.reducer;